import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Header from "../../../components/feature/Header";
import Footer from "../../../components/feature/Footer";
import SEO from "../../../components/base/SEO";
import { useTranslation } from "react-i18next";

interface Article {
  id: string;
  slug: string;
  title: string;
  titleEn: string;
  description: string;
  descriptionEn: string;
  category: string;
  tags: string[];
  author: string;
  publishDate: string;
  readTime: string;
  thumbnail: string;
  htmlFile: string;
}

export default function BlogArticle() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();
  const isZh = t("nav.language") === "中";
  const [article, setArticle] = useState<Article | null>(null);
  const [relatedArticles, setRelatedArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch("/blog/articles.json")
      .then((res) => res.json())
      .then((data) => {
        const found = data.articles.find((a: Article) => a.slug === slug);
        setArticle(found || null);
        // Get related articles (same category, excluding current)
        if (found) {
          const related = data.articles
            .filter(
              (a: Article) => a.category === found.category && a.slug !== slug,
            )
            .slice(0, 3);
          setRelatedArticles(related);
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error("Failed to load article:", err);
        setLoading(false);
      });
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-blue-600 animate-spin"></i>
          <p className="mt-4 text-gray-600">
            {isZh ? "載入中..." : "Loading..."}
          </p>
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-20 text-center">
          <i className="ri-file-unknow-line text-6xl text-gray-300 mb-4"></i>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            {isZh ? "找不到文章" : "Article Not Found"}
          </h1>
          <Link to="/knowledge" className="text-blue-600 hover:underline">
            {isZh ? "返回文章列表" : "Back to Blog"}
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={`${isZh ? article.title : article.titleEn} | AUTOCHAIN 物流知識庫`}
        description={isZh ? article.description : article.descriptionEn}
        keywords={article.tags.join(", ")}
        canonicalUrl={`/knowledge/${article.slug}`}
        ogImage={
          article.thumbnail
            ? `https://autochain.com.hk${article.thumbnail}`
            : undefined
        }
      />

      {/* Structured Data - Article */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: isZh ? article.title : article.titleEn,
            description: isZh ? article.description : article.descriptionEn,
            image: article.thumbnail
              ? `https://autochain.com.hk${article.thumbnail}`
              : "https://autochain.com.hk/logo.png",
            author: {
              "@type": "Organization",
              name: article.author,
            },
            publisher: {
              "@type": "Organization",
              name: "AUTOCHAIN TECHNOLOGY",
              logo: {
                "@type": "ImageObject",
                url: "https://autochain.com.hk/logo.png",
              },
            },
            datePublished: article.publishDate,
            dateModified: article.publishDate,
            mainEntityOfPage: {
              "@type": "WebPage",
              "@id": `https://autochain.com.hk/knowledge/${article.slug}`,
            },
            articleSection: article.category,
            keywords: article.tags,
          }),
        }}
      />

      {/* Structured Data - Breadcrumb */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "首頁",
                item: "https://autochain.com.hk/",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "物流知識庫",
                item: "https://autochain.com.hk/knowledge",
              },
              {
                "@type": "ListItem",
                position: 3,
                name: isZh ? article.title : article.titleEn,
                item: `https://autochain.com.hk/knowledge/${article.slug}`,
              },
            ],
          }),
        }}
      />

      <Header />

      {/* Breadcrumb */}
      <div className="bg-gray-50 py-4">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center text-sm text-gray-500">
            <Link to="/" className="hover:text-blue-600">
              {isZh ? "首頁" : "Home"}
            </Link>
            <i className="ri-arrow-right-s-line mx-2"></i>
            <Link to="/knowledge" className="hover:text-blue-600">
              {isZh ? "物流知識庫" : "Knowledge Hub"}
            </Link>
            <i className="ri-arrow-right-s-line mx-2"></i>
            <span className="text-gray-900 truncate max-w-xs">
              {isZh ? article.title : article.titleEn}
            </span>
          </nav>
        </div>
      </div>

      {/* Hero Image */}
      {article.thumbnail && (
        <section className="py-8">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <img
              src={article.thumbnail}
              alt={isZh ? article.title : article.titleEn}
              className="w-full h-auto rounded-2xl shadow-lg object-cover"
            />
          </div>
        </section>
      )}

      {/* Article Content (iframe) */}
      <section className="py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <iframe
            src={`/blog/${article.htmlFile}`}
            title={article.title}
            className="w-full border-0 rounded-lg"
            style={{ minHeight: "800px" }}
            onLoad={(e) => {
              const iframe = e.target as HTMLIFrameElement;
              try {
                const doc = iframe.contentWindow?.document;
                if (doc) {
                  const resizableIframe = iframe as HTMLIFrameElement & {
                    __autochainIframeCleanup?: () => void;
                  };

                  resizableIframe.__autochainIframeCleanup?.();

                  // Remove left/right padding from body
                  doc.body.style.paddingLeft = "0";
                  doc.body.style.paddingRight = "0";
                  doc.documentElement.style.overflowY = "hidden";
                  doc.body.style.overflowY = "hidden";

                  // Auto-adjust iframe height as images and embedded content load.
                  const resizeIframe = () => {
                    const height = Math.max(
                      doc.body.scrollHeight,
                      doc.body.offsetHeight,
                    );

                    if (height) {
                      iframe.style.height = `${height + 50}px`;
                    }
                  };

                  let animationFrame = 0;
                  const scheduleResize = () => {
                    if (animationFrame) {
                      window.cancelAnimationFrame(animationFrame);
                    }

                    animationFrame = window.requestAnimationFrame(() => {
                      animationFrame = 0;
                      resizeIframe();
                    });
                  };

                  const resizeObserver =
                    typeof ResizeObserver !== "undefined"
                      ? new ResizeObserver(scheduleResize)
                      : null;
                  resizeObserver?.observe(doc.body);

                  const images = Array.from(doc.images);
                  images.forEach((image) => {
                    image.addEventListener("load", scheduleResize);
                    image.addEventListener("error", scheduleResize);
                  });

                  iframe.contentWindow?.addEventListener(
                    "resize",
                    scheduleResize,
                  );
                  doc.fonts?.ready.then(scheduleResize).catch(() => {});

                  const timeoutIds = [100, 500, 1500, 3000].map((delay) =>
                    window.setTimeout(scheduleResize, delay),
                  );

                  scheduleResize();

                  resizableIframe.__autochainIframeCleanup = () => {
                    if (animationFrame) {
                      window.cancelAnimationFrame(animationFrame);
                    }

                    resizeObserver?.disconnect();
                    images.forEach((image) => {
                      image.removeEventListener("load", scheduleResize);
                      image.removeEventListener("error", scheduleResize);
                    });
                    iframe.contentWindow?.removeEventListener(
                      "resize",
                      scheduleResize,
                    );
                    timeoutIds.forEach(window.clearTimeout);
                  };
                }
              } catch {
                // Cross-origin restriction, use default height
              }
            }}
          />
        </div>
      </section>

      {/* Tags */}
      <section className="py-8 border-t">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-gray-600 text-sm mr-2">
              {isZh ? "標籤：" : "Tags:"}
            </span>
            {article.tags.map((tag) => (
              <span
                key={tag}
                className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm"
              >
                #{tag}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Related Articles */}
      {relatedArticles.length > 0 && (
        <section className="py-12 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-8">
              {isZh ? "相關文章" : "Related Articles"}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedArticles.map((related) => (
                <Link
                  key={related.id}
                  to={`/knowledge/${related.slug}`}
                  className="bg-white rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow"
                >
                  <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2 hover:text-blue-600">
                    {isZh ? related.title : related.titleEn}
                  </h3>
                  <p className="text-sm text-gray-500">{related.publishDate}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-12 bg-blue-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold text-white mb-4">
            {isZh
              ? "想了解更多？聯絡我們！"
              : "Want to Learn More? Contact Us!"}
          </h2>
          <button
            onClick={() => {
              const message = encodeURIComponent(
                isZh
                  ? `您好！我剛看完「${article.title}」這篇文章，想了解更多。`
                  : `Hello! I just read "${article.titleEn}" and would like to learn more.`,
              );
              window.open(
                `https://wa.me/85294252903?text=${message}`,
                "_blank",
              );
            }}
            className="inline-flex items-center px-6 py-3 bg-white text-blue-600 font-medium rounded-lg hover:bg-gray-100 transition-colors"
          >
            <i className="ri-whatsapp-line text-xl mr-2"></i>
            {isZh ? "WhatsApp 聯絡" : "Contact via WhatsApp"}
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
